// AuthMeProvider — single source of truth for the AuthMeResponse cache so
// every consumer (Sidebar, TopBar, Dashboard, …) reads from one fetch
// instead of mounting independent /api/auth/me loaders. Wraps the
// authenticated branch of the route tree above AppShell.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useMsal } from "@azure/msal-react";
import type { AuthMeResponse } from "../../../shared/types/auth";
import { getAccessToken } from "./getToken";
import { loginRequest } from "./msalConfig";

export interface UseAuthResult {
  user: AuthMeResponse | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  getAccessToken: () => Promise<string>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthMeContext = createContext<UseAuthResult | null>(null);

export function AuthMeProvider({ children }: { children: ReactNode }) {
  const { instance } = useMsal();
  const [user, setUser] = useState<AuthMeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessToken();
      const res = await fetch("/api/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`/api/auth/me ${res.status}`);
      setUser((await res.json()) as AuthMeResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (instance.getAllAccounts().length > 0) void refresh();
    else setLoading(false);
  }, [instance, refresh]);

  const value = useMemo<UseAuthResult>(
    () => ({
      user,
      loading,
      error,
      isAdmin: user?.isAdmin ?? false,
      getAccessToken,
      signIn: () => instance.loginRedirect(loginRequest),
      signOut: () => instance.logoutRedirect(),
      refresh,
    }),
    [user, loading, error, instance, refresh],
  );

  return (
    <AuthMeContext.Provider value={value}>{children}</AuthMeContext.Provider>
  );
}

export function useAuth(): UseAuthResult {
  const ctx = useContext(AuthMeContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthMeProvider>");
  }
  return ctx;
}
